import { ILC_EVENT_RELAY_HOST as relayHost, ILC_EVENT_RELAY_PATH as relayPath } from 'environment'
import { useCurrentCustomerStore, useCurrentUserStore } from 'vue_features/shared/store/composables'
import { ensureValidToken, initializeToken } from './event_relay/jwt_token_manager'
import { EventQueue } from './event_relay/event_queue'
import { BatchScheduler } from './event_relay/batch_scheduler'
import { EventSender } from './event_relay/event_sender'
import debug from 'debug'

const relayUrl = `${relayHost}${relayPath}`
const log = debug('app:javascript/clients/event_relay.js')

export class EventRelay {
  constructor(config = {}) {
    this.queue = new EventQueue(config.batchSize || 20)
    this.scheduler = new BatchScheduler(config.batchTimeout || 15000)
    this.sender = new EventSender(config.url || relayUrl, config.tokenProvider || ensureValidToken)
    this.canSend = config.canSend || (() => this.canSendEventRelay())
    this.maxRetryAttempts = config.maxRetryAttempts || 3
    this.scheduler.setFlushCallback(() => this.sendBatch())

    /* This is to catch page exit and empty the queue */
    window.addEventListener('pagehide', () => {
      this.sendBatch({ keepalive: true })
    })
  }

  sendEvent(name, data = {}) {
    if (!this.canSend()) return

    const event = this.buildEvent(name, data)
    const shouldSendImmediately = this.queue.add(event)

    if (shouldSendImmediately) {
      this.sendBatch()
    } else {
      this.scheduler.schedule()
    }
  }

  async sendBatch(options = {}) {
    if (this.queue.isEmpty()) return

    if (!this.canSend()) {
      log('Event relay disabled, clearing queue')
      this.queue.flush()
      return
    }

    const events = this.queue.flush()
    this.scheduler.cancel()

    try {
      await this.sender.send(
        events.map((e) => e.payload),
        options,
      )
    } catch (error) {
      this.restoreFailuresIfNotStale(events)
      log('Error sending batch:', error)
    }
  }

  buildEvent(name, data) {
    return {
      payload: {
        event: name,
        data,
        eventTime: new Date().toISOString(),
        customerId: useCurrentCustomerStore().id.value,
        userId: useCurrentUserStore().id.value,
        referrer: {
          href: location.href,
          title: document.title,
        },
      },
      retryCount: 0,
    }
  }

  canSendEventRelay() {
    return this.validUrl() && !useCurrentUserStore().impersonated.value
  }

  validUrl() {
    try {
      new URL(this.sender.url)
      return true
    } catch {
      return false
    }
  }

  flush() {
    return this.sendBatch()
  }

  getQueueSize() {
    return this.queue.size()
  }

  restoreFailuresIfNotStale(failedEvents) {
    const eventsWithUpdatedRetryCount = failedEvents.map(this.updateRetryCount)
    const nonStaleEvents = eventsWithUpdatedRetryCount.filter(this.removeIfStale.bind(this))
    if (nonStaleEvents.length > 0) {
      this.queue.restore(nonStaleEvents)
    }
  }

  updateRetryCount(failedEvent) {
    return {
      ...failedEvent,
      retryCount: failedEvent.retryCount + 1,
    }
  }

  removeIfStale(failedEvent) {
    return failedEvent.retryCount >= this.maxRetryAttempts
  }
}

initializeToken()

const eventRelay = new EventRelay()

export function sendEvent(name, data = {}) {
  eventRelay.sendEvent(name, data)
}
